import { ApiClient, ApiError, type RunpodCustomNode } from '../lib/api.js';
import {
  el,
  formatBytes,
  formatDateTime,
  mount,
} from '../lib/dom.js';

export interface RunpodCustomNodesViewDeps {
  api: ApiClient;
}

export function renderRunpodCustomNodes(deps: RunpodCustomNodesViewDeps): HTMLElement {
  const card = el('section', { class: 'card' });
  const list = el('div', { class: 'logs-list' });
  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  // ── Upload form ─────────────────────────────────────────
  const fileInput = el('input', { attrs: { type: 'file', accept: '.zip,application/zip' } });
  const nameInput = el('input', {
    attrs: {
      type: 'text',
      placeholder: 'Name (e.g. ComfyUI-Manager)',
      spellcheck: false,
      autocapitalize: 'off',
      autocorrect: 'off',
    },
  });
  const descInput = el('input', {
    attrs: {
      type: 'text',
      placeholder: 'Description (optional)',
      maxlength: 500,
    },
  });
  const uploadBtn = el('button', {
    class: 'primary',
    text: 'Upload .zip',
    attrs: { type: 'button' },
  });

  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    if (!nameInput.value.trim()) {
      // Pre-fill with the file's basename, sans .zip.
      const base = f.name.replace(/\.zip$/i, '').replace(/[^A-Za-z0-9._-]+/g, '-');
      nameInput.value = base;
    }
  });

  uploadBtn.addEventListener('click', async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      showAlert('Pick a .zip file first');
      return;
    }
    const name = nameInput.value.trim();
    if (!name) {
      showAlert('Set a name for this custom node');
      nameInput.focus();
      return;
    }
    uploadBtn.setAttribute('disabled', '');
    const original = uploadBtn.textContent;
    uploadBtn.textContent = `Uploading ${formatBytes(file.size)}…`;
    try {
      const desc = descInput.value.trim();
      const payload: { name: string; file: File; filename: string; description?: string } = {
        name,
        file,
        filename: file.name,
      };
      if (desc) payload.description = desc;
      await deps.api.uploadRunpodCustomNode(payload);
      showAlert(`Uploaded "${name}"`, 'success');
      fileInput.value = '';
      nameInput.value = '';
      descInput.value = '';
      await refresh();
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      uploadBtn.removeAttribute('disabled');
      uploadBtn.textContent = original ?? 'Upload .zip';
    }
  });

  const uploadCard = el('section', {
    class: 'card',
    children: [
      el('header', {
        children: [
          el('h2', { text: 'Upload custom node' }),
          el('span', {
            class: 'who',
            text: 'Server-side store of personal ComfyUI custom-node ZIPs',
          }),
        ],
      }),
      el('div', {
        class: 'form-row',
        children: [
          el('label', {
            class: 'field',
            children: [el('span', { text: 'ZIP file' }), fileInput],
          }),
          el('label', {
            class: 'field',
            children: [el('span', { text: 'Name' }), nameInput],
          }),
          el('label', {
            class: 'field',
            children: [el('span', { text: 'Description' }), descInput],
          }),
          uploadBtn,
        ],
      }),
      el('p', {
        class: 'hint',
        text:
          'Each name maps to /ComfyUI/custom_nodes/<name>/ inside the built image. '
          + 'The .zip is extracted and any requirements.txt is pip-installed at build time.',
      }),
    ],
  });

  // ── Existing nodes list ─────────────────────────────────
  const renderRow = (n: RunpodCustomNode): HTMLElement => {
    const head = el('div', {
      class: 'log-row-head',
      children: [
        el('span', { class: 'branch-tag', text: n.name }),
        el('span', {
          class: 'who',
          text: `${formatBytes(n.sizeBytes)} · ${n.attachedBranches.length} endpoint(s)`,
        }),
      ],
    });
    const meta = el('div', {
      class: 'when',
      text: [
        n.originalFilename,
        formatDateTime(n.uploadedAt),
        n.uploadedByUsername ? `by ${n.uploadedByUsername}` : null,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });
    const description = n.description
      ? el('div', { class: 'who', text: n.description })
      : null;
    const branches = n.attachedBranches.length > 0
      ? el('div', {
          class: 'who',
          text: `Used by: ${n.attachedBranches.join(', ')}`,
        })
      : el('div', {
          class: 'who',
          text: 'Not attached to any endpoint',
        });
    const sha = el('div', {
      class: 'who mono',
      text: `sha256: ${n.sha256.slice(0, 16)}…`,
    });
    const metaCol = el('div', {
      class: 'log-meta',
      children: [head, meta, description, branches, sha].filter((x) => x !== null) as HTMLElement[],
    });

    const downloadBtn = el('a', {
      class: 'btn',
      text: 'Download',
      attrs: {
        href: deps.api.runpodCustomNodeDownloadUrl(n.id),
        download: n.originalFilename,
      },
    });
    // <a> elements don't render with the same default styling as <button>
    // unless we promote them via the .btn class. Wrap in a span so the
    // log-actions flex container behaves the same as the other rows.
    const deleteBtn = el('button', {
      class: 'danger',
      text: 'Delete',
      on: {
        click: async () => {
          if (n.attachedBranches.length > 0) {
            showAlert(
              `Detach "${n.name}" from ${n.attachedBranches.length} endpoint(s) first`,
            );
            return;
          }
          if (!confirm(`Delete custom node "${n.name}"? This cannot be undone.`)) return;
          try {
            await deps.api.deleteRunpodCustomNode(n.id);
            showAlert(`Deleted "${n.name}"`, 'success');
            await refresh();
          } catch (err) {
            showAlert(err instanceof ApiError ? err.message : 'Delete failed');
          }
        },
      },
    });

    return el('div', {
      class: 'log-row',
      children: [metaCol, el('div', { class: 'log-actions', children: [downloadBtn, deleteBtn] })],
    });
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items } = await deps.api.listRunpodCustomNodes();
      summary.textContent = items.length === 0
        ? 'No custom nodes yet'
        : `${items.length} node(s)`;
      if (items.length === 0) {
        mount(list, [el('div', { class: 'empty', text: 'No custom nodes uploaded yet.' })]);
      } else {
        mount(list, items.map(renderRow));
      }
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load custom nodes');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };
  refreshBtn.addEventListener('click', () => void refresh());

  card.append(
    el('header', {
      children: [
        el('div', { class: 'modal-title', children: [el('h2', { text: 'Custom nodes' }), summary] }),
        el('div', { class: 'log-actions', children: [refreshBtn] }),
      ],
    }),
    alertBox,
    list,
  );

  void refresh();

  return el('div', { children: [uploadCard, card] });
}
